import { cloneDeep, isEmpty } from 'lodash';
import sha256 from 'crypto-js/sha256';
import { message } from 'antd';

import { TABLE_TYPES } from '@/lib/tableType';
import { getDataFromLocalStorage, putDataIntoLocalStorage } from '@/lib/localStorage';

const TABLE_SETTINGS_BY_WELL = 'tableSettingsByWell';
const TABLE_SETTINGS_BY_FIELD = 'tableSettingsByField';
const COLUMNS_WIDTH_POUCH_DB_KEY = 'columnsWidth';
const DEFAULT_COLUMN_WIDTH = 100;

const defineColumnsWidth = async (columnsByType, columnsWidth = null) => {
    let columnsActualWidth = null;

    const columnsWidthPdb = getDataFromLocalStorage({ key: COLUMNS_WIDTH_POUCH_DB_KEY });
    if (columnsWidthPdb && !columnsWidthPdb.error && columnsWidthPdb.data && !columnsWidth) {
        columnsActualWidth = columnsWidthPdb.data;
    } else {
        const maxColumnsLength = Object.values(columnsByType ?? {}).reduce(
            (acc, columns) => Math.max(acc, (columns ?? []).length),
            0,
        );
        if (maxColumnsLength) {
            columnsActualWidth = Array.from(
                { length: maxColumnsLength },
                (_, ix) => columnsWidth?.[ix] ?? DEFAULT_COLUMN_WIDTH,
            );
        }
    }
    return columnsActualWidth;
};

const tableWidgetModel = {
    state: {
        activeStratums: null,
        columns: null,
        columnsWidth: null,
        dataSource: null,
        stratums: null,
        groupPeriod: 1,
        groupSubtotal: 12,
        groupTotal: false,
        settings: {
            currentType: TABLE_TYPES.oil,
            rawColumns: null,
            /* Поле groups сделано для управления настройками групп, которые не влияют на таблицу */
            groups: null,
            templateColumns: null,
            defaultTemplateColumns: null,
            selectedTemplateColumns: null,
            period: 1,
            subtotal: 12,
            total: false,
        },
        loading: {
            dataSource: false,
        },
        error: {
            rawColumns: false,
            dataSource: false,
        },
    },
    reducers: {
        setActiveStratums(state, payload) {
            return {
                ...state,
                activeStratums: payload,
            };
        },
        setColumns(state, payload) {
            return {
                ...state,
                columns: payload,
            };
        },
        setColumnsWidth(state, payload) {
            if (payload) putDataIntoLocalStorage({ key: COLUMNS_WIDTH_POUCH_DB_KEY, data: payload });
            return { ...state, columnsWidth: payload };
        },
        setDataSource(state, payload) {
            return {
                ...state,
                dataSource: payload,
            };
        },
        setStratums(state, payload) {
            return { ...state, stratums: payload };
        },
        setGroupPeriod(state, payload) {
            return {
                ...state,
                groupPeriod: payload,
            };
        },
        setGroupSubtotal(state, payload) {
            return {
                ...state,
                groupSubtotal: payload,
            };
        },
        setGroupTotal(state, payload) {
            return {
                ...state,
                groupTotal: payload,
            };
        },
        setGroupParams(state, { groupPeriod = null, groupSubtotal = null, groupTotal = null }) {
            return {
                ...state,
                groupPeriod,
                groupSubtotal,
                groupTotal,
            };
        },
        setSettings(state, payload) {
            return {
                ...state,
                settings: payload,
            };
        },
        setSettingsCurrentType(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    currentType: payload,
                    selectedTemplateColumns: null,
                    // rawColumns: null,
                },
            };
        },
        setSettingsRawColumns(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    rawColumns: payload,
                },
            };
        },
        setSettingsGroups(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    groups: payload,
                },
            };
        },
        setSettingsTemplateColumns(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    templateColumns: payload,
                },
            };
        },
        setSettingsDefaultTemplateColumns(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    defaultTemplateColumns: payload,
                },
            };
        },
        setSettingsSelectedTemplateColumns(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    selectedTemplateColumns: payload,
                },
            };
        },
        setSettingsPeriod(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    period: payload,
                },
            };
        },
        setSettingsSubtotal(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    subtotal: payload,
                },
            };
        },
        setSettingsTotal(state, payload) {
            return {
                ...state,
                settings: {
                    ...state.settings,
                    total: payload,
                },
            };
        },
        setLoadingDataSource(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    dataSource: payload,
                },
            };
        },
        setErrorRawColumns(state, payload) {
            return {
                ...state,
                error: {
                    ...state.error,
                    rawColumns: payload,
                },
            };
        },
        setErrorDataSource(state, payload) {
            return {
                ...state,
                error: {
                    ...state.error,
                    dataSource: payload,
                },
            };
        },

        setColumnsAndWidth(state, payload) {
            return {
                ...state,
                columns: payload.columns,
                columnsWidth: payload.columnsWidth,
            };
        },
    },
    effects: dispatch => ({
        async setColumnsAndColumnsWidth(payload, rootState) {
            const columnsWidth = await defineColumnsWidth(payload, rootState.tableWidget.columnsWidth);

            dispatch.tableWidget.setColumnsAndWidth({ columns: payload, columnsWidth: columnsWidth });
        },

        convertSettingsRawColumns(props) {
            const { data } = props;
            try {
                const filteredData = Object.values(data).reduce((accData, characteristic) => {
                    if (
                        characteristic.destination === undefined ||
                        (Array.isArray(characteristic.destination) &&
                            characteristic.destination?.some(type => type === 'table'))
                    )
                        accData[characteristic.key] = characteristic;

                    return accData;
                }, {});
                dispatch.tableWidget.setSettingsRawColumns(filteredData);
            } catch (error) {
                message.error('Не удалось получить показатели для таблицы');
                dispatch.tableWidget.setErrorRawColumns(true);
            }
        },

        createGroupsSettings(headers) {
            if (isEmpty(headers)) return;
            const groupsSettings = Object.keys(headers).reduce((acc, key) => {
                const header = headers[key];
                const groups = header
                    .filter(item => item.kind === 'group')
                    .reduce((acc, { key, isOpen }) => ({ ...acc, [key]: { key, isOpen } }), {});
                return { ...acc, [key]: groups };
            }, {});
            this.setSettingsGroups(groupsSettings);
        },

        async addToSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            if (!newTemplateColumns[currentType]) {
                newTemplateColumns[currentType] = [];
            }
            if (newTemplateColumns[currentType].findIndex(item => item.key === payload.key) === -1) {
                const newItem = {
                    ...payload,
                    kind: 'root',
                    isEnabled: true,
                    isEditing: false,
                };
                newTemplateColumns[currentType].push(newItem);
                dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
            }
        },
        async removeFromSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(item => item.key === payload.key);
            // First level
            if (index !== -1) {
                newTemplateColumns[currentType] = [
                    ...newTemplateColumns[currentType].slice(0, index),
                    ...newTemplateColumns[currentType].slice(index + 1),
                ];
            } else {
                // Second level
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(item => item.key === payload.key);
                        if (childIndex !== -1) {
                            element.children = [
                                ...element.children.slice(0, childIndex),
                                ...element.children.slice(childIndex + 1),
                            ];
                        }
                    }
                    return element;
                });
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
            dispatch.tableWidget.setSettingsSelectedTemplateColumns(null);
        },
        async toggleEnabledFromSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, rawColumns, currentType } = rootState.tableWidget.settings;

            // TODO: for future
            // if (isEmpty(payload.children)) {
            //     this.setSettingsRawColumns({
            //         ...rawColumns,
            //         [payload.key]: { ...rawColumns[payload.key], isEnabled: !rawColumns[payload.key] },
            //     });
            // }

            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(item => item.key === payload.key);
            // First level
            if (index !== -1) {
                newTemplateColumns[currentType][index].isEnabled = !newTemplateColumns[currentType][index].isEnabled;
                // Second level
                const item = newTemplateColumns[currentType][index];
                if (item.kind === 'group' && item?.children?.length) {
                    newTemplateColumns[currentType][index].children = item.children.map(child => {
                        child.isEnabled = newTemplateColumns[currentType][index].isEnabled;
                        return child;
                    });
                }
            } else {
                // Second level
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(item => item.key === payload.key);
                        if (childIndex !== -1 && element.isEnabled) {
                            element.children[childIndex].isEnabled = !element.children[childIndex].isEnabled;
                        }
                    }
                    return element;
                });
            }

            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async toggleAllEnabledFromSettingsTemplateColumns(_, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : [];
            const isAllEnabled =
                !newTemplateColumns[currentType].some(item => item.isEnabled) ||
                !newTemplateColumns[currentType].every(item => item.isEnabled);
            newTemplateColumns[currentType].map(item => {
                item.isEnabled = isAllEnabled;
                // Second level
                if (item.kind === 'group' && item?.children?.length) {
                    item.children = item.children.map(child => {
                        child.isEnabled = isAllEnabled;
                        return child;
                    });
                }
                return item;
            });
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async addNewGroupToSettingsTemplateColumns(_, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            if (!newTemplateColumns[currentType]) {
                newTemplateColumns[currentType] = [];
            }
            const key = sha256(`group-${Math.random()}`).toString();
            const newGroup = {
                title: 'Новая папка',
                key,
                type: 'none',
                kind: 'group',
                isOpen: false,
                isEnabled: true,
                isEditing: true,
                children: [],
            };
            newTemplateColumns[currentType].push(newGroup);
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
            this.createGroupsSettings(newTemplateColumns);
            dispatch.tableWidget.setSettingsSelectedTemplateColumns(key);
        },
        async toggleOpenGroupFromSettingsTemplateColumns(payload, rootState) {
            const { currentType, groups } = rootState.tableWidget.settings;
            const { key, kind } = payload;
            if (kind === 'group') {
                this.setSettingsGroups({
                    ...groups,
                    [currentType]: {
                        ...groups[currentType],
                        [key]: { ...groups[currentType][key], isOpen: !groups[currentType][key].isOpen },
                    },
                });
            }
        },
        async editTitleFromSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(item => item.key === payload.key);
            // First level
            if (index !== -1) {
                newTemplateColumns[currentType][index].isEditing = true;
            } else {
                // Second level
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(item => item.key === payload.key);
                        if (childIndex !== -1) {
                            element.children[childIndex].isEditing = true;
                        }
                    }
                    return element;
                });
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async saveTitleFromSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const { item, newTitle } = payload;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(el => el.key === item.key);
            // First level
            if (index !== -1) {
                newTemplateColumns[currentType][index] = {
                    ...item,
                    title: newTitle,
                    isEditing: false,
                };
            } else {
                // Second level
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(el => el.key === item.key);
                        if (childIndex !== -1) {
                            element.children[childIndex] = {
                                ...item,
                                title: newTitle,
                                isEditing: false,
                            };
                        }
                    }
                    return element;
                });
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async moveUpFromSettingsTemplateColumns(_, rootState) {
            const { templateColumns, currentType, groups, selectedTemplateColumns } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(el => el.key === selectedTemplateColumns);
            // First level
            if (index !== -1) {
                if (index === 0) {
                    const item = newTemplateColumns[currentType].shift();
                    newTemplateColumns[currentType].push(item);
                } else {
                    const prevIndex = index - 1;
                    if (
                        newTemplateColumns[currentType][index].kind !== 'group' &&
                        newTemplateColumns[currentType][prevIndex].kind === 'group' &&
                        groups[currentType][newTemplateColumns[currentType][prevIndex].key].isOpen
                    ) {
                        // Add to group
                        const item = {
                            ...newTemplateColumns[currentType][index],
                            parent: newTemplateColumns[currentType][prevIndex].key,
                            kind: 'child',
                        };
                        newTemplateColumns[currentType][prevIndex].children.push(item);
                        newTemplateColumns[currentType] = [
                            ...newTemplateColumns[currentType].slice(0, index),
                            ...newTemplateColumns[currentType].slice(index + 1),
                        ];
                    } else {
                        const item = newTemplateColumns[currentType][index];
                        newTemplateColumns[currentType][index] = newTemplateColumns[currentType][prevIndex];
                        newTemplateColumns[currentType][prevIndex] = item;
                    }
                }
            } else {
                // Second level
                let childItem;
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(el => el.key === selectedTemplateColumns);
                        if (childIndex !== -1) {
                            if (childIndex === 0) {
                                childItem = element.children.shift();
                            } else {
                                const prevChildIndex = childIndex - 1;
                                const item = element.children[childIndex];
                                element.children[childIndex] = element.children[prevChildIndex];
                                element.children[prevChildIndex] = item;
                            }
                        }
                    }
                    return element;
                });

                // Leave from group
                if (childItem) {
                    const parentIndex = newTemplateColumns[currentType].findIndex(el => el.key === childItem.parent);
                    newTemplateColumns[currentType] = [
                        ...newTemplateColumns[currentType].slice(0, parentIndex),
                        {
                            ...childItem,
                            kind: 'root',
                            parent: null,
                        },
                        ...newTemplateColumns[currentType].slice(parentIndex),
                    ];
                }
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async moveDownFromSettingsTemplateColumns(_, rootState) {
            const { templateColumns, currentType, groups, selectedTemplateColumns } = rootState.tableWidget.settings;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            if (newTemplateColumns === null) return;
            const index = newTemplateColumns[currentType].findIndex(el => el.key === selectedTemplateColumns);
            const lastIndex = newTemplateColumns[currentType]?.length - 1;
            // First level
            if (index !== -1) {
                if (index === lastIndex) {
                    const item = newTemplateColumns[currentType].pop();
                    newTemplateColumns[currentType].unshift(item);
                } else {
                    const nextIndex = index + 1;
                    if (
                        newTemplateColumns[currentType][index].kind !== 'group' &&
                        newTemplateColumns[currentType][nextIndex].kind === 'group' &&
                        groups[currentType][newTemplateColumns[currentType][nextIndex].key].isOpen
                    ) {
                        // Add to group
                        const item = {
                            ...newTemplateColumns[currentType][index],
                            parent: newTemplateColumns[currentType][nextIndex].key,
                            kind: 'child',
                        };
                        newTemplateColumns[currentType][nextIndex].children.unshift(item);
                        newTemplateColumns[currentType] = [
                            ...newTemplateColumns[currentType].slice(0, index),
                            ...newTemplateColumns[currentType].slice(index + 1),
                        ];
                    } else {
                        const item = newTemplateColumns[currentType][index];
                        newTemplateColumns[currentType][index] = newTemplateColumns[currentType][nextIndex];
                        newTemplateColumns[currentType][nextIndex] = item;
                    }
                }
            } else {
                // Second level
                let childItem;
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element?.children?.findIndex(el => el.key === selectedTemplateColumns);
                        const childLastIndex = element?.children?.length - 1;
                        if (childIndex !== -1) {
                            if (childIndex === childLastIndex) {
                                childItem = element.children.pop();
                            } else {
                                const nextChildIndex = childIndex + 1;
                                const item = element.children[childIndex];
                                element.children[childIndex] = element.children[nextChildIndex];
                                element.children[nextChildIndex] = item;
                            }
                        }
                    }
                    return element;
                });

                // Leave from group
                if (childItem) {
                    const parentIndex = newTemplateColumns[currentType].findIndex(el => el.key === childItem.parent);
                    newTemplateColumns[currentType] = [
                        ...newTemplateColumns[currentType].slice(0, parentIndex + 1),
                        {
                            ...childItem,
                            kind: 'root',
                            parent: null,
                        },
                        ...newTemplateColumns[currentType].slice(parentIndex + 1),
                    ];
                }
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async setAccuracyFromSettingsTemplateColumns(payload, rootState) {
            const { templateColumns, currentType } = rootState.tableWidget.settings;
            const { item, accuracy } = payload;
            const newTemplateColumns = templateColumns ? cloneDeep(templateColumns) : null;
            const index = newTemplateColumns[currentType].findIndex(el => el.key === item.key);
            // First level
            if (index !== -1) {
                newTemplateColumns[currentType][index] = {
                    ...item,
                    accuracy,
                };
            } else {
                // Second level
                newTemplateColumns[currentType].map(element => {
                    if (element.kind === 'group') {
                        const childIndex = element.children.findIndex(el => el.key === item.key);
                        if (childIndex !== -1) {
                            element.children[childIndex] = {
                                ...item,
                                accuracy,
                            };
                        }
                    }
                    return element;
                });
            }
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
            // const { templateColumns, currentType, rawColumns } = rootState.tableWidget.settings;
            // const { item, accuracy } = payload;

            // if (!isEmpty(item) && !isEmpty(rawColumns)) {
            //     this.setSettingsRawColumns({ ...rawColumns, [item.key]: { ...item, accuracy: accuracy } });
            // }
        },
        async clearSettingsTemplateColumns(props, rootState) {
            const { defaultTemplateColumns } = rootState.tableWidget.settings;
            if (!defaultTemplateColumns) return;
            const newTemplateColumns = cloneDeep(defaultTemplateColumns);
            this.createGroupsSettings(newTemplateColumns);
            dispatch.tableWidget.setSettingsTemplateColumns(newTemplateColumns);
        },
        async saveSettings(props, rootState) {
            const { templateColumns, period, subtotal, total } = rootState.tableWidget.settings;
            await dispatch.tableWidget.setColumnsAndColumnsWidth(templateColumns);
            dispatch.tableWidget.setGroupParams({ groupPeriod: period, groupSubtotal: subtotal, groupTotal: total });
            dispatch.tableWidget.saveLocalSettings(props);
        },

        loadParamsFromIDB({ isField }, rootState) {
            const settingsJSON = localStorage.getItem(isField ? TABLE_SETTINGS_BY_FIELD : TABLE_SETTINGS_BY_WELL);
            const settings = JSON.parse(settingsJSON);
            let params = {};
            if (settings) {
                const { period: step, subtotal: total, total: sum } = settings;
                params = { step, total, sum };
            } else {
                const { groupPeriod: step, groupSubtotal: total, groupTotal: sum } = rootState.tableWidget;
                params = { step, total, sum };
            }
            return params;
        },

        async loadLocalSettings(props) {
            const { isField = false, defaultSettings = null } = props;
            const settingsJSON = localStorage.getItem(isField ? TABLE_SETTINGS_BY_FIELD : TABLE_SETTINGS_BY_WELL);
            const settings = JSON.parse(settingsJSON);
            if (settings) {
                dispatch.tableWidget.setSettings(settings);
                const { templateColumns, period, subtotal, total, groups } = settings;

                await dispatch.tableWidget.setColumnsAndColumnsWidth(templateColumns);
                this.setSettingsGroups(groups);
                dispatch.tableWidget.setGroupParams({
                    groupPeriod: period,
                    groupSubtotal: subtotal,
                    groupTotal: total,
                });
            } else {
                const templateColumnsSettings = defaultSettings;
                this.setSettingsTemplateColumns(templateColumnsSettings);
                if (templateColumnsSettings) {
                    await dispatch.tableWidget.setColumnsAndColumnsWidth(templateColumnsSettings);
                    this.createGroupsSettings(templateColumnsSettings);
                }
            }
        },
        async saveLocalSettings(props, rootState) {
            const { isField } = props;
            const { settings } = rootState.tableWidget;
            // settings.rawColumns = null;
            const settingsJSON = JSON.stringify(settings);
            localStorage.setItem(isField ? TABLE_SETTINGS_BY_FIELD : TABLE_SETTINGS_BY_WELL, settingsJSON);
        },
    }),
    selectors: slice => ({
        stratumsList() {
            return slice(({ stratums }) => Object.values(stratums ?? {}));
        },
        settingsTemplateColumns() {
            return slice(({ settings: { templateColumns, currentType } }) =>
                templateColumns ? templateColumns[currentType] : null,
            );
        },
    }),
};

export default tableWidgetModel;
